import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Icon, Kbd, Pill, Skeleton } from '@etnas/design-system';
import { patientsApi, type PatientListItem } from '@/api';
import { Modal } from '@/components/Modal';
import { ErrorState } from '@/components/States';
import { usePatientSlots } from '../patientSlots';
import { useAuth } from '../auth';
import { workspaceRail, patientHome } from '../workspaces';
import { fmtDate } from '@/lib/format';
import { sexShort } from '@/lib/patient';

function useDebounced<T>(v: T, ms: number) {
  const [d, setD] = useState(v);
  useEffect(() => {
    const t = setTimeout(() => setD(v), ms);
    return () => clearTimeout(t);
  }, [v, ms]);
  return d;
}

const norm = (s: string) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

/**
 * Command bar (DS `ex-cmd` markup) with live patient search. Identity safeguards (guideline 50):
 * warns when more than one patient matches the same name and always shows two identifiers (DOB, MRN).
 */
export function CommandSearch({ onClose }: { onClose: () => void }) {
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const dq = useDebounced(q.trim(), 220);
  const { openPatient } = usePatientSlots();
  const { me } = useAuth();
  const nav = useNavigate();
  const role = me?.context.role.key;
  const canSearch = !!me?.permissions.some(p => p === 'patient.search' || p === 'patient.lookup');
  const listRef = useRef<HTMLDivElement>(null);
  const res = useQuery({ queryKey: ['patient-search', dq], queryFn: () => patientsApi.search({ q: dq, limit: 12 }), enabled: canSearch && dq.length >= 2 });

  const patients = res.data?.items ?? [];
  const nameCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const p of patients) {
      const k = norm(p.name.family) + '|' + norm(p.name.given[0] ?? '').slice(0, 3);
      m.set(k, (m.get(k) ?? 0) + 1);
    }
    return m;
  }, [patients]);
  const sameName = patients.filter((p) => (nameCounts.get(norm(p.name.family) + '|' + norm(p.name.given[0] ?? '').slice(0, 3)) ?? 0) > 1);
  const sameDob = patients.some((p, i) => patients.findIndex((x) => x.dob === p.dob && norm(x.name.family) === norm(p.name.family)) !== i);

  const actions = useMemo(() => {
    const a: { label: string; sub?: string; icon: string; run: () => void }[] = [];
    if (me?.permissions.includes('patient.create')) a.push({ label: 'Register a new patient', sub: 'Checks for duplicates before creating', icon: 'plus', run: () => nav('/registration/new') });
    if (role && me) for (const r of workspaceRail(role, me.context.roleId, me.permissions)) if (!q || norm(r.label).includes(norm(q))) a.push({ label: `Go to ${r.label}`, icon: r.icon, run: () => nav(r.to) });
    return a.slice(0, 5);
  }, [role, me, nav, q]);

  const items: ({ kind: 'patient'; p: PatientListItem } | { kind: 'action'; a: (typeof actions)[number] })[] = [
    ...patients.map((p) => ({ kind: 'patient' as const, p })),
    ...actions.map((a) => ({ kind: 'action' as const, a })),
  ];

  const choose = (i: number) => {
    const it = items[i];
    if (!it) return;
    onClose();
    if (it.kind === 'action') return it.a.run();
    if (!role) return;
    const to = patientHome(role, it.p.id, me?.context.roleId);
    if (to) openPatient({ id: it.p.id, display: it.p.name.display, meta: `${it.p.age.replace(' y', '')}${sexShort(it.p.sex)}` }, to);
  };

  return (
    <Modal onClose={onClose} initialFocus="#cmd-input">
      <div className="ex-cmd" role="dialog" aria-modal="true" aria-label="Search patients and actions">
        <div className="ex-cmd-input">
          <Icon name="search" size={16} />
          <input
            id="cmd-input"
            className="ex-cmd-field"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setActive(0);
            }}
            placeholder="Name, MRN or date of birth (YYYY-MM-DD)"
            aria-label="Search patients, actions and places"
            role="combobox"
            aria-expanded={items.length > 0}
            aria-controls="cmd-list"
            aria-activedescendant={items.length ? `cmd-opt-${active}` : undefined}
            autoComplete="off"
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setActive((a) => Math.min(items.length - 1, a + 1));
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setActive((a) => Math.max(0, a - 1));
              } else if (e.key === 'Enter') {
                e.preventDefault();
                choose(active);
              }
            }}
          />
          <Kbd>Esc</Kbd>
        </div>
        {sameName.length > 1 || sameDob ? (
          <div className="ex-cmd-warn" role="alert">
            <Icon name="urgent" size={12} knockout="var(--urgent-soft)" />
            {sameName.length} patients match this name{sameDob ? ', some with the same date of birth' : ''}. Confirm date of birth and MRN before opening.
          </div>
        ) : null}
        <div className="ex-cmd-list" id="cmd-list" role="listbox" aria-label="Results" ref={listRef}>
          {canSearch && dq.length >= 2 && res.isPending ? <Skeleton rows={3} cols={[50, 20, 20]} /> : null}
          {res.isError ? <ErrorState error={res.error} what="patient search" inline /> : null}
          {dq.length >= 2 && res.isSuccess && !patients.length ? (
            <div className="ex-empty-line">No patients in {me?.tenant?.name ?? 'this organization'} match “{dq}”. Check spelling, or search by MRN or date of birth.</div>
          ) : null}
          {patients.length ? (
            <div className="ex-cmd-group">
              <div className="ex-lbl">Patients</div>
              {patients.map((p, i) => {
                const twinLike = sameName.includes(p);
                return (
                  <button
                    key={p.id}
                    id={`cmd-opt-${i}`}
                    type="button"
                    role="option"
                    aria-selected={active === i}
                    className={'ex-cmd-item is-btn' + (active === i ? ' is-active' : '')}
                    onMouseEnter={() => setActive(i)}
                    onClick={() => choose(i)}
                  >
                    <Icon name="user" size={16} />
                    <span className="ex-cmd-main">
                      <span className="ex-strong">{p.name.display}</span>
                      <span className="ex-small ex-fg-muted">
                        DOB <span className="ex-mono">{p.dob}</span> ({fmtDate(p.dob)}) · {p.age} · {sexShort(p.sex)} · MRN <span className="ex-mono">{p.mrn}</span>
                        {p.city ? ` · ${p.city}` : ''}
                      </span>
                    </span>
                    {p.deceased ? <Pill tone="muted" size="sm">Deceased</Pill> : null}
                    {p.identityStatus === 'confidential' ? <Pill tone="sensitive" size="sm" icon="shield">Confidential</Pill> : null}
                    {p.identityStatus === 'unverified' ? <Pill tone="urgent" size="sm" icon="info">ID not verified</Pill> : null}
                    {p.identityStatus === 'temporary' ? <Pill tone="urgent" size="sm" icon="urgent">Temporary</Pill> : null}
                    {twinLike ? <Pill tone="urgent" size="sm" icon="users">Similar name</Pill> : null}
                  </button>
                );
              })}
            </div>
          ) : null}
          {actions.length ? (
            <div className="ex-cmd-group">
              <div className="ex-lbl">Actions and places</div>
              {actions.map((a, j) => {
                const i = patients.length + j;
                return (
                  <button key={a.label} id={`cmd-opt-${i}`} type="button" role="option" aria-selected={active === i} className={'ex-cmd-item is-btn' + (active === i ? ' is-active' : '')} onMouseEnter={() => setActive(i)} onClick={() => choose(i)}>
                    <Icon name={a.icon} size={16} />
                    <span className="ex-cmd-main">
                      <span className="ex-strong">{a.label}</span>
                      {a.sub ? <span className="ex-small ex-fg-muted">{a.sub}</span> : null}
                    </span>
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>
        <div className="ex-cmd-foot">
          <span>
            <Kbd>↑</Kbd>
            <Kbd>↓</Kbd> move
          </span>
          <span>
            <Kbd>↵</Kbd> open in a slot
          </span>
          <span>Every search is recorded in the access log</span>
        </div>
      </div>
    </Modal>
  );
}
